import React, { memo, useState, useCallback } from 'react';
import { Task, TaskStatus } from '../../types';
import { useProject } from '../../context/ProjectContext';
import { useTaskActions } from '../../hooks/useTaskActions';
import { getAvatarUrl } from '../../utils/formatters';
import { PriorityBadge, getPriorityColor } from './PrioritySelector';
import { ChecklistProgress } from './TaskChecklist';
import { TaskMoveMenu } from './TaskMoveMenu';
import { TaskEditModal } from './TaskEditModal';

// ============================================
// Types
// ============================================

interface TaskCardProps {
  task: Task;
}

// ============================================
// Component
// ============================================

export const TaskCard: React.FC<TaskCardProps> = memo(({ task }) => {
  const { users } = useProject();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Use the task actions hook
  const {
    formState,
    setFormState,
    resetForm,
    newItemText,
    setNewItemText,
    toggleChecklistItem,
    removeChecklistItem,
    addChecklistItem,
    handleSave,
    handleDelete,
    handleMoveStatus,
    isSaving,
    isDeleting,
    isAdminOrManager,
    justCompleted,
  } = useTaskActions(task);

  // Get assignee info
  const assignee = users.find(u => u.id === task.assigneeId);
  const avatarUrl = assignee?.avatar || getAvatarUrl(task.assigneeId);

  // Checklist stats
  const completedItems = task.checklist?.filter(i => i.completed).length || 0;
  const totalItems = task.checklist?.length || 0;

  // Is task completed
  const isDone = task.status === TaskStatus.DONE;

  // Drag handlers
  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.dataTransfer.setData('taskId', task.id);
  }, [task.id]);

  // Modal handlers
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    resetForm();
    setIsModalOpen(false);
  }, [resetForm]);

  const handleFormChange = useCallback((updates: Partial<typeof formState>) => {
    setFormState(prev => ({ ...prev, ...updates }));
  }, [setFormState]);

  return (
    <>
      {/* Task Card */}
      <div 
        draggable 
        onDragStart={handleDragStart}
        onClick={openModal}
        className={`
          bg-white p-4 md:p-6 rounded-3xl 
          border border-slate-100 shadow-sm 
          hover:shadow-xl transition-all duration-300 
          cursor-grab active:cursor-grabbing 
          group animate-in zoom-in-95 
          relative overflow-hidden
          ${justCompleted ? 'ring-4 ring-emerald-500/30 scale-105 shadow-emerald-100' : ''}
        `}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && openModal()}
        aria-label={`Task: ${task.title}`}
      >
        {/* Priority Indicator Bar */}
        <div 
          className={`absolute top-0 left-0 w-1 md:w-1.5 h-full ${getPriorityColor(task.priority)} opacity-60`} 
        />
        
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          {/* Badges */}
          <div className="flex flex-wrap gap-1.5">
            <PriorityBadge priority={task.priority} />
            <ChecklistProgress completed={completedItems} total={totalItems} />
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1 relative">
            {/* Mobile Quick Move */}
            <TaskMoveMenu 
              currentStatus={task.status}
              onMove={handleMoveStatus}
            />
            
            {/* Desktop Edit Button */}
            <button 
              onClick={(e) => { e.stopPropagation(); openModal(); }}
              className="hidden md:block text-slate-300 hover:text-indigo-600 transition-colors p-1"
              aria-label="Edit task"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Title */}
        <h4 
          className={`
            font-black mb-1.5 leading-tight transition-all text-sm md:text-base
            ${isDone 
              ? 'text-slate-400 line-through' 
              : 'text-slate-900 group-hover:text-indigo-600'
            }
          `}
        >
          {task.title}
        </h4>
        
        {/* Description */}
        <p 
          className={`
            text-[11px] md:text-xs line-clamp-2 mb-4 leading-relaxed font-medium transition-colors
            ${isDone ? 'text-slate-300' : 'text-slate-500'}
          `}
        >
          {task.description}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50">
          {/* Assignee */}
          <div className="flex items-center gap-2 min-w-0">
            <img 
              src={avatarUrl}
              className={`w-7 h-7 rounded-full border border-white shadow-sm object-cover ${isDone ? 'grayscale' : ''}`} 
              alt=""
              loading="lazy"
            />
            <span 
              className={`text-[11px] font-bold truncate ${isDone ? 'text-slate-400' : 'text-slate-800'}`}
            >
              {assignee?.name || 'Unassigned'}
            </span>
          </div>
          
          {/* Estimate */}
          <span className="text-[10px] font-black text-slate-400 bg-slate-50 px-2 py-1 rounded-lg">
            {task.estimateHours}h
          </span>
        </div>
      </div>

      {/* Edit Modal */}
      <TaskEditModal
        isOpen={isModalOpen}
        onClose={closeModal}
        task={task}
        formState={formState}
        onFormChange={handleFormChange}
        onSave={handleSave}
        onDelete={handleDelete}
        users={users}
        newItemText={newItemText}
        onNewItemChange={setNewItemText}
        onToggleChecklistItem={toggleChecklistItem}
        onRemoveChecklistItem={removeChecklistItem}
        onAddChecklistItem={addChecklistItem}
        isSaving={isSaving}
        isDeleting={isDeleting}
        canDelete={isAdminOrManager}
        isDeleteConfirmOpen={isDeleteConfirmOpen}
        onOpenDeleteConfirm={() => setIsDeleteConfirmOpen(true)}
        onCloseDeleteConfirm={() => setIsDeleteConfirmOpen(false)}
      />
    </>
  );
});

TaskCard.displayName = 'TaskCard';

export default TaskCard;
